import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  Bell,
  CheckCheck,
  Trash2,
  Settings,
  Play,
  MessageCircle,
  Heart,
  Users,
  AlertCircle,
  CheckCircle2,
  Clock,
  Search,
  SlidersHorizontal,
  RefreshCw
} from 'lucide-react';
import { useNotificationsStore } from '@/lib/stores/notifications';
import type { Notification } from '@/lib/stores/notifications/types';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { getNotificationNavigation } from '@/lib/utils/navigation-helpers';
import { handleNotificationNavigation } from '@/lib/utils/notification-auth';

// Notification type options based on the spec
const NOTIFICATION_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'stream_live', label: 'Stream Live' },
  { value: 'stream_scheduled', label: 'Stream Scheduled' },
  { value: 'stream_ended', label: 'Stream Ended' },
  { value: 'access_granted', label: 'Access Granted' },
  { value: 'access_denied', label: 'Access Denied' },
  { value: 'invite_received', label: 'Invite Received' },
  { value: 'comment_reply', label: 'Comment Reply' },
  { value: 'comment_mention', label: 'Mention' },
  { value: 'comment_liked', label: 'Comment Liked' },
  { value: 'moderation_action', label: 'Moderation' },
  { value: 'system_announcement', label: 'Announcement' },
  { value: 'system', label: 'System' },
] as const;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    error,
    unreadCount,
    pagination,
    fetchNotifications,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    setFilters,
    filters,
    clearError
  } = useNotificationsStore();

  const sentinelRef = useRef<HTMLDivElement>(null);

  const [selectedTab, setSelectedTab] = useState<'all' | 'unread' | 'read'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  // Safety defaults for SSR/hydration - stores may be undefined initially
  const safeNotifications = Array.isArray(notifications) ? notifications : [];

  // Calculate this week's notifications count
  const thisWeekNotifications = useMemo(() => {
    if (!sevenDaysAgo) return 0;
    return safeNotifications.filter((n) => {
      const notificationDate = new Date(n.createdAt);
      return notificationDate > sevenDaysAgo;
    }).length;
  }, [safeNotifications, sevenDaysAgo]);

  // Calculate read count
  const readCount = useMemo(() => {
    return safeNotifications.filter((n) => n.read).length;
  }, [safeNotifications]);

  const fetchData = useCallback(async () => {
    try {
      // Build filter params
      const filterParams: Record<string, string> = {};
      
      if (selectedTab === 'unread') {
        filterParams.read = 'false';
      } else if (selectedTab === 'read') {
        filterParams.read = 'true';
      }
      
      if (typeFilter !== 'all') {
        filterParams.type = typeFilter;
      }
      
      await fetchNotifications(filterParams);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, [fetchNotifications, selectedTab, typeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Infinite scroll: observe sentinel div at bottom of list
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && pagination?.hasMore && !isLoading) {
          loadMoreNotifications();
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [pagination?.hasMore, isLoading, loadMoreNotifications]);

  const handleMarkAsRead = async (notificationId: string | number) => {
    try {
      await markAsRead(typeof notificationId === 'string' ? parseInt(notificationId) : notificationId);
    } catch (err) {
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (notificationId: string | number) => {
    try {
      await deleteNotification(typeof notificationId === 'string' ? parseInt(notificationId) : notificationId);
    } catch (err) {
      toast.error('Failed to delete notification');
    }
  };

  const handleRetry = () => {
    clearError();
    fetchData();
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread and the backend is reachable
    if (!notification.read && !api.isBackendUnavailable()) {
      await handleMarkAsRead(notification.id);
    }
    
    // Get target URL and verify access before navigation
    const targetUrl = getNotificationNavigation(notification);
    if (targetUrl) {
      await handleNotificationNavigation(targetUrl, (safeUrl) => {
        navigate(safeUrl);
      });
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'stream_live':
        return <Play className="h-4 w-4 text-red-500" />;
      case 'stream_scheduled':
      case 'stream_ended':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'new_comment':
      case 'comment':
        return <MessageCircle className="h-4 w-4 text-blue-500" />;
      case 'comment_reply':
        return <MessageCircle className="h-4 w-4 text-green-500" />;
      case 'comment_mention':
      case 'mention':
        return <Users className="h-4 w-4 text-purple-500" />;
      case 'comment_liked':
      case 'like':
        return <Heart className="h-4 w-4 text-pink-500" />;
      case 'access_granted':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'access_denied':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'invite_received':
        return <Users className="h-4 w-4 text-orange-500" />;
      case 'moderation_action':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'system_announcement':
      case 'system':
        return <Settings className="h-4 w-4 text-gray-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const getNotificationTypeLabel = (type: string) => {
    const option = NOTIFICATION_TYPE_OPTIONS.find(opt => opt.value === type);
    if (option) return option.label;
    
    switch (type) {
      case 'stream_live':
        return 'Stream Live';
      case 'stream_scheduled':
        return 'Stream Scheduled';
      case 'stream_ended':
        return 'Stream Ended';
      case 'new_comment':
      case 'comment':
        return 'New Comment';
      case 'comment_reply':
        return 'Comment Reply';
      case 'comment_mention':
      case 'mention':
        return 'Mention';
      case 'comment_liked':
      case 'like':
        return 'Comment Liked';
      case 'access_granted':
        return 'Access Granted';
      case 'access_denied':
        return 'Access Denied';
      case 'invite_received':
        return 'Invite Received';
      case 'moderation_action':
        return 'Moderation';
      case 'system_announcement':
        return 'Announcement';
      case 'system':
        return 'System';
      default:
        return 'Notification';
    }
  };

  // Filter notifications by search query
  const filteredNotifications = useMemo(() => {
    if (!searchQuery.trim()) return safeNotifications;
    
    const query = searchQuery.toLowerCase();
    return safeNotifications.filter(n =>
      n.title?.toLowerCase().includes(query) ||
      n.message?.toLowerCase().includes(query)
    );
  }, [safeNotifications, searchQuery]);

  if (isLoading && safeNotifications.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="@container/notifications max-w-[1800px] w-full mx-auto px-3 py-4 @[740px]/notifications:px-4 @[740px]/notifications:py-5 @[1100px]/notifications:px-6 @[1100px]/notifications:py-6 space-y-4 @[740px]/notifications:space-y-5 @[1100px]/notifications:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl tracking-tight">Notifications</h1>
        </div>

        <div className="flex items-center gap-2">
          {safeNotifications.length > 0 && (
            <Button
              variant="outline"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0 || isLoading}
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
          )}
          <Button variant="outline" onClick={handleRetry} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <p className="text-destructive">{error}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleRetry} disabled={isLoading}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))'
        }}
      >
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Notifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{safeNotifications.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Unread</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{unreadCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Read</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground">{readCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {thisWeekNotifications}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Layout: sidebar + list */}
      <div className="flex flex-col @[740px]/notifications:flex-row @[740px]/notifications:gap-6 @[740px]/notifications:items-start">

        {/* Desktop Sidebar */}
        <aside className="hidden @[740px]/notifications:flex @[740px]/notifications:flex-col @[740px]/notifications:w-52 @[740px]/notifications:shrink-0 gap-4">

          {/* Status filter */}
          <div>
            <p className="px-2 pb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</p>
            {(
              [
                { value: 'all', label: 'All', count: safeNotifications.length },
                { value: 'unread', label: 'Unread', count: unreadCount },
                { value: 'read', label: 'Read', count: readCount },
              ] as const
            ).map(({ value, label, count }) => (
              <Button
                key={value}
                variant="ghost"
                className={`w-full justify-between h-9 px-2 ${selectedTab === value ? 'bg-accent text-accent-foreground' : ''}`}
                onClick={() => setSelectedTab(value)}
              >
                <span>{label}</span>
                <Badge variant="secondary" className="text-xs min-w-[1.5rem] justify-center">{count}</Badge>
              </Button>
            ))}
          </div>

          <div className="border-t" />

          {/* Type filter */}
          <div>
            <p className="px-2 pb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Type</p>
            <div className="space-y-0.5">
              {NOTIFICATION_TYPE_OPTIONS.map(({ value, label }) => (
                <Button
                  key={value}
                  variant="ghost"
                  className={`w-full justify-start h-8 px-2 text-sm font-normal ${typeFilter === value ? 'bg-accent text-accent-foreground' : ''}`}
                  onClick={() => setTypeFilter(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {(searchQuery.trim() || typeFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => { setSearchQuery(''); setTypeFilter('all'); }}
            >
              Clear filters
            </Button>
          )}
        </aside>

        {/* Main panel */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* Mobile: status tabs + Sheet filter */}
          <div className="@[740px]/notifications:hidden space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Tabs value={selectedTab} onValueChange={(value) => setSelectedTab(value as 'all' | 'unread' | 'read')}>
                <TabsList>
                  <TabsTrigger value="all">All ({safeNotifications.length})</TabsTrigger>
                  <TabsTrigger value="unread">Unread ({unreadCount})</TabsTrigger>
                  <TabsTrigger value="read">Read ({readCount})</TabsTrigger>
                </TabsList>
              </Tabs>
              <Sheet open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm">
                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                    Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[min(92vw,420px)] p-0">
                  <SheetHeader className="border-b p-4 pr-12">
                    <SheetTitle>Notification Filters</SheetTitle>
                    <SheetDescription>Refine notifications by search text and type.</SheetDescription>
                  </SheetHeader>
                  <div className="space-y-4 p-4">
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Search</p>
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search notifications..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Type</p>
                      <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="Filter by type" />
                        </SelectTrigger>
                        <SelectContent>
                          {NOTIFICATION_TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* Desktop: search bar */}
          <div className="hidden @[740px]/notifications:block">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Notification list */}
          <div>
            {filteredNotifications.length === 0 && !error ? (
              <div className="text-center py-12">
                <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">
                  {searchQuery ? 'No matching notifications' :
                    selectedTab === 'unread' ? 'No unread notifications' :
                    selectedTab === 'read' ? 'No read notifications' : 'No notifications yet'
                  }
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery
                    ? 'Try adjusting your search terms'
                    : selectedTab === 'unread'
                      ? "You're all caught up! Check back later for new updates."
                      : selectedTab === 'read'
                        ? "You haven't read any notifications yet"
                        : "When you receive notifications, they'll appear here."
                  }
                </p>
                {selectedTab === 'unread' && unreadCount === 0 && safeNotifications.length > 0 && (
                  <Button variant="outline" onClick={() => setSelectedTab('all')}>
                    View All Notifications
                  </Button>
                )}
              </div>
            ) : !error ? (
              <div className="space-y-4">
                {filteredNotifications.map((notification) => {
                  const targetUrl = getNotificationNavigation(notification);
                  const isClickable = targetUrl !== null;

                  return (
                    <div
                      key={notification.id}
                      className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                        notification.read
                          ? 'bg-background border-border'
                          : 'bg-muted/50 border-primary/20'
                      } ${isClickable ? 'cursor-pointer hover:bg-muted/70' : ''}`}
                      onClick={isClickable ? () => handleNotificationClick(notification) : undefined}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onKeyDown={isClickable ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleNotificationClick(notification);
                        }
                      } : undefined}
                      aria-label={isClickable ? `Navigate to ${notification.title}` : undefined}
                    >
                      <div className="shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                {getNotificationTypeLabel(notification.type)}
                              </Badge>
                              {!notification.read && (
                                <div className="w-2 h-2 bg-primary rounded-full" />
                              )}
                            </div>

                            <h4 className="font-medium mb-1">{notification.title}</h4>
                            <p className="text-sm text-muted-foreground mb-2">
                              {notification.message}
                            </p>

                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {new Date(notification.createdAt).toLocaleString()}
                              </span>
                              {targetUrl && (
                                <span className="text-primary">
                                  {notification.actionLabel || 'View Details'}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            {!notification.read && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMarkAsRead(notification.id)}
                                className="h-8 px-2"
                              >
                                <CheckCheck className="h-4 w-4 mr-1" />
                                Mark Read
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(notification.id)}
                              className="h-8 px-2 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="h-1" />

                {/* Loading more indicator */}
                {isLoading && safeNotifications.length > 0 && (
                  <div className="flex justify-center py-4">
                    <LoadingSpinner size="sm" />
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Notification Settings Quick Access */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Notification Settings
          </CardTitle>
          <CardDescription>
            Customize how and when you receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Control which events send you email or in-app notifications. Visit Settings to configure your preferences.
          </p>

          <div className="mt-6 pt-6 border-t">
            <Button variant="outline" asChild>
              <Link to="/settings?tab=notifications">
                <Settings className="h-4 w-4 mr-2" />
                Manage Notification Settings
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
